import { shallowEqual } from 'react-redux';
import { useStudioProjectContext } from 'pages/dashboard/studio/StudioProjectProvider';
import { AppState } from 'store';
import { Section } from '../../../../../server/graphql/__generated.types';
import useProjectSections from '../../Sections/hooks/useProjectSections';
import useSectionViewOptions from '../../Sections/hooks/useSectionViewOptions';
import {
  getSectionBaseFormattedText,
  SectionViewOptions,
} from '../../Sections/selectors';
import { useAppSelector } from '../../../../store/hooks';

export interface SectionFormattedText {
  sectionId: string;
  formattedText: string;
}

/*
 * Resolve the formatted text to show for a section: the in-progress draft if
 * there is one and it isn't stale, otherwise the section's saved text (see
 * `getSectionBaseFormattedText`).
 */
export const resolveSectionFormattedText = (
  section: Section | undefined | null,
  draft: AppState['scriptStudioBuffers']['byId'][string] | undefined,
  options: SectionViewOptions = {}
): SectionFormattedText | null => {
  if (!section) return null;

  const outdatedDraft =
    draft?.lastEditedAt && section.currentTake?.createdAt
      ? draft.lastEditedAt < Number(section.currentTake.createdAt)
      : false;

  if (draft?.formattedTextDraft && !outdatedDraft) {
    return { sectionId: section.id, formattedText: draft.formattedTextDraft };
  }

  const hasSavedText =
    !!section.currentTake || (!!options.readSectionEditor && !!section.editor);

  return hasSavedText
    ? {
        sectionId: section.id,
        formattedText: getSectionBaseFormattedText(section, options),
      }
    : null;
};

/*
 * get the formatted text for the given section ids, live.
 * Use useGetSectionsFormattedText instead when the result is only needed
 * inside an event handler rather than rendered.
 */
const useSectionsFormattedText = (ids: string[]): SectionFormattedText[] => {
  const { projectId } = useStudioProjectContext();
  const { sections } = useProjectSections({ projectId });
  const sectionViewOptions = useSectionViewOptions();

  // Narrowed to just the requested ids so unrelated section drafts
  // (e.g. another section being typed in) don't trigger a re-render here.
  const relevantDraftsById = useAppSelector(state => {
    const drafts: AppState['scriptStudioBuffers']['byId'] = {};
    ids.forEach(id => {
      const draft = state.scriptStudioBuffers.byId[id];
      if (draft) drafts[id] = draft;
    });
    return drafts;
  }, shallowEqual);

  return sections
    .map(section => {
      if (!section || !ids.includes(section.id)) return null;
      return resolveSectionFormattedText(
        section,
        relevantDraftsById[section.id],
        sectionViewOptions
      );
    })
    .filter((section): section is SectionFormattedText => section !== null);
};

export default useSectionsFormattedText;
